import { useEffect, useRef, useState } from "react";
import { Plus, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { invalidateSiteContent } from "@/lib/content";

const inputClass =
  "w-full bg-surface border border-line px-4 py-3 text-sm text-bone placeholder:text-fog/40 focus:border-gold focus:outline-none transition-colors duration-300";

const norm = (c) => (typeof c === "string" ? { name: c, logo: "" } : c);
const normT = (t) => (typeof t === "string" ? { quote: t, author: "" } : t);

const LogoCell = ({ value, onChange, index }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    setUploading(true);
    try {
      const res = await api.post("/admin/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(res.data.url);
      toast.success("Logo caricato");
    } catch {
      toast.error("Upload non riuscito");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div className="w-28 shrink-0">
      <div className="relative h-12 border border-line bg-coal flex items-center justify-center overflow-hidden">
        {value ? (
          <>
            <img src={value} alt="" className="max-h-8 max-w-[90%] object-contain" />
            <button
              type="button"
              data-testid={`client-logo-clear-${index}`}
              onClick={() => onChange("")}
              className="absolute top-0.5 right-0.5 w-5 h-5 bg-night/80 flex items-center justify-center text-fog hover:text-red-400"
              aria-label="Rimuovi logo"
            >
              <X size={10} />
            </button>
          </>
        ) : (
          <span className="text-[9px] font-mono text-fog/40">{uploading ? "CARICO…" : "NESSUN LOGO"}</span>
        )}
      </div>
      <button
        type="button"
        data-testid={`client-logo-upload-${index}`}
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        className="mt-1.5 w-full inline-flex items-center justify-center gap-1.5 px-2 py-1.5 border border-line text-fog text-[9px] tracking-[0.1em] hover:text-gold hover:border-gold transition-[color,border-color] duration-300"
      >
        <Upload size={10} /> LOGO
      </button>
      <input ref={inputRef} type="file" accept="image/*" onChange={handleFile} className="hidden" />
    </div>
  );
};

const ClientsAdmin = () => {
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/content").then((res) => setContent(res.data)).catch(() => toast.error("Errore nel caricamento"));
  }, []);

  const clients = (content?.clients || []).map(norm);
  const testimonials = (content?.testimonials || []).map(normT);

  const update = (index, patch) =>
    setContent((prev) => ({ ...prev, clients: prev.clients.map((c, i) => (i === index ? { ...norm(c), ...patch } : c)) }));

  const removeClient = (index) =>
    setContent((prev) => ({ ...prev, clients: prev.clients.filter((_, i) => i !== index) }));

  const addClient = () =>
    setContent((prev) => ({ ...prev, clients: [...(prev.clients || []), { name: "", logo: "" }] }));

  const updateT = (index, patch) =>
    setContent((prev) => ({ ...prev, testimonials: (prev.testimonials || []).map((t, i) => (i === index ? { ...normT(t), ...patch } : t)) }));

  const removeT = (index) =>
    setContent((prev) => ({ ...prev, testimonials: prev.testimonials.filter((_, i) => i !== index) }));

  const addT = () =>
    setContent((prev) => ({ ...prev, testimonials: [...(prev.testimonials || []), { quote: "", author: "" }] }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...content,
        clients: clients.map((c) => ({ name: c.name.trim(), logo: c.logo })).filter((c) => c.name || c.logo),
        testimonials: testimonials.map((t) => ({ quote: t.quote.trim(), author: t.author.trim() })).filter((t) => t.quote || t.author),
      };
      await api.put("/admin/content", payload);
      setContent(payload);
      invalidateSiteContent();
      toast.success("Clienti e testimonianze aggiornati sul sito");
    } catch {
      toast.error("Salvataggio non riuscito");
    } finally {
      setSaving(false);
    }
  };

  if (!content) return <p className="text-xs font-mono tracking-[0.25em] text-fog">CARICAMENTO…</p>;

  return (
    <div data-testid="admin-clients-page" className="max-w-3xl">
      <h1 className="text-2xl font-normal tracking-tight text-bone">Clienti</h1>
      <p className="mt-1 text-sm text-fog">
        La sezione "Alcuni clienti" della home. Per ogni cliente puoi scrivere il nome e caricare il file logo (PNG con sfondo trasparente funziona meglio). Se carichi il logo, sul sito compare l'immagine al posto del nome.
      </p>

      <div className="mt-8 space-y-3">
        {clients.map((client, i) => (
          <div key={i} data-testid={`client-row-${i}`} className="flex gap-3 items-start">
            <span className="w-8 pt-4 text-[10px] font-mono text-fog shrink-0">{String(i + 1).padStart(2, "0")}</span>
            <LogoCell value={client.logo} onChange={(url) => update(i, { logo: url })} index={i} />
            <input
              data-testid={`client-input-${i}`}
              value={client.name}
              onChange={(e) => update(i, { name: e.target.value })}
              placeholder="Nome del cliente"
              className={inputClass}
            />
            <button
              type="button"
              data-testid={`client-remove-${i}`}
              onClick={() => removeClient(i)}
              className="mt-1 px-3 py-3 border border-line text-fog hover:text-red-400 hover:border-red-400 transition-[color,border-color] duration-300 shrink-0"
              aria-label="Elimina cliente"
            >
              <X size={14} />
            </button>
          </div>
        ))}
      </div>

      <button
        type="button"
        data-testid="client-add-button"
        onClick={addClient}
        className="mt-5 inline-flex items-center gap-2 px-5 py-3 border border-dashed border-line text-fog text-xs tracking-[0.15em] hover:text-gold hover:border-gold transition-[color,border-color] duration-300"
      >
        <Plus size={14} /> AGGIUNGI CLIENTE
      </button>

      <h2 className="mt-16 text-2xl font-normal tracking-tight text-bone">Testimonianze</h2>
      <p className="mt-1 text-sm text-fog">I feedback dei clienti mostrati sotto i loghi. Puoi aggiungerne quante ne vuoi.</p>

      <div className="mt-8 space-y-5">
        {testimonials.map((t, i) => (
          <div key={i} data-testid={`testimonial-row-${i}`} className="flex gap-3 items-start">
            <span className="w-8 pt-4 text-[10px] font-mono text-fog shrink-0">{String(i + 1).padStart(2, "0")}</span>
            <div className="flex-1 space-y-2">
              <textarea
                data-testid={`testimonial-quote-${i}`}
                rows={3}
                value={t.quote}
                onChange={(e) => updateT(i, { quote: e.target.value })}
                placeholder="Testo della testimonianza"
                className={`${inputClass} resize-none`}
              />
              <input
                data-testid={`testimonial-author-${i}`}
                value={t.author}
                onChange={(e) => updateT(i, { author: e.target.value })}
                placeholder="Firma (es. Marketing Director — Brand)"
                className={inputClass}
              />
            </div>
            <button
              type="button"
              data-testid={`testimonial-remove-${i}`}
              onClick={() => removeT(i)}
              className="mt-1 px-3 py-3 border border-line text-fog hover:text-red-400 hover:border-red-400 transition-[color,border-color] duration-300 shrink-0"
              aria-label="Elimina testimonianza"
            >
              <X size={14} />
            </button>
          </div>
        ))}
      </div>

      <button
        type="button"
        data-testid="testimonial-add-button"
        onClick={addT}
        className="mt-5 inline-flex items-center gap-2 px-5 py-3 border border-dashed border-line text-fog text-xs tracking-[0.15em] hover:text-gold hover:border-gold transition-[color,border-color] duration-300"
      >
        <Plus size={14} /> AGGIUNGI TESTIMONIANZA
      </button>

      <div className="mt-10">
        <button
          onClick={handleSave}
          data-testid="clients-save-button"
          disabled={saving}
          className="px-10 py-4 bg-gold text-night text-xs font-semibold tracking-[0.2em] hover:bg-bone disabled:opacity-60 transition-colors duration-300"
        >
          {saving ? "SALVATAGGIO…" : "SALVA TUTTO"}
        </button>
      </div>
    </div>
  );
};

export default ClientsAdmin;
